import { zodResolver } from '@hookform/resolvers/zod';
import { createCardBodySchema, type CreateCardBody } from '@klopotun/contracts';
import type { FormEvent } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  Button,
  ColorField,
  SelectField,
  TextField,
  type SelectOption,
} from '../../../../shared/ui/index.js';
import { toCardBodyDraft } from '../../lib/card-form-adapter.js';
import { cardFormSchema } from '../../lib/card-form-schema.js';
import type { CardFormValues } from '../../model/card-form-values.js';
import styles from './card-form.module.css';

const COMMON_CURRENCIES = ['UAH', 'USD', 'EUR', 'PLN', 'GBP'];

const currencyOptionsOf = (selected: string): SelectOption[] => {
  const codes = [...new Set([selected, ...COMMON_CURRENCIES])].filter((code) => code !== '');

  return codes.map((code) => ({ value: code, label: code }));
};

type CardFormProps = {
  initialValues: CardFormValues;
  onSubmit: (body: CreateCardBody) => void;
  onCancel: () => void;
  busy: boolean;
};

export const CardForm = ({ initialValues, onSubmit, onCancel, busy }: CardFormProps) => {
  const { control, handleSubmit } = useForm<CardFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(cardFormSchema),
    mode: 'onTouched',
  });
  const currency = useWatch({ control, name: 'currency' });

  const save = handleSubmit((values) => {
    onSubmit(createCardBodySchema.parse(toCardBodyDraft(values)));
  });

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    void save(event);
  };

  return (
    <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
      <div className={styles.fields}>
        <Controller
          control={control}
          name="label"
          render={({ field, fieldState }) => (
            <TextField
              label="Назва"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Чорна зарплатна"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="last4"
          render={({ field, fieldState }) => (
            <TextField
              label="Останні чотири цифри"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              inputMode="numeric"
              placeholder="4441"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="brand"
          render={({ field, fieldState }) => (
            <TextField
              label="Платіжна система"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Visa"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="expiresMonth"
          render={({ field, fieldState }) => (
            <TextField
              label="Діє до, місяць"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              inputMode="numeric"
              placeholder="11"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="expiresYear"
          render={({ field, fieldState }) => (
            <TextField
              label="Діє до, рік"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              inputMode="numeric"
              placeholder="2029"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="currency"
          render={({ field, fieldState }) => (
            <SelectField
              label="Валюта"
              name={field.name}
              options={currencyOptionsOf(currency)}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Не вказано"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="color"
          render={({ field, fieldState }) => (
            <ColorField
              label="Колір"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              hint="Не обов'язково"
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <div className={styles.actions}>
        <Button type="submit" busy={busy}>
          Зберегти
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Скасувати
        </Button>
      </div>
    </form>
  );
};
